// Thin wrapper over @workspace/messaging-ui's MessageList (tdd-0050). Keeps the
// exact same props ChatWidget already passes, so nothing upstream changes. This
// file now owns only the chat-specific glue: filtering internal messages, mapping
// Message → ChatMessage, installing the AI renderer registry, and the streaming
// "Thinking…" footer.

import { useMemo } from "react";
import type { Message as MessageType } from "../../types";
import type { UserInfo } from "../../hooks";
import {
  MessageList,
  MessageRenderersProvider,
} from "@workspace/messaging-ui";
import { chatRenderers, toChatMessage } from "./message-renderers";
import { ComponentRendererProvider } from "./component-segment";
import { createGenUIRenderer } from "./gen-ui-renderer";
import { submitFormValues, type SubmitOutcome } from "../../api/forms";

interface ConversationProps {
  messages: MessageType[];
  isLoading: boolean;
  appName?: string;
  modelId?: string;
  userInfo?: UserInfo | null;
  thinkingDisplayMode?: "collapsed" | "expanded";
  groupMessages?: boolean;
  /** Undo a saved memory (delete from Brain + clear the saved bubble). */
  onDeleteMemory?: (messageId: string) => void;
  /** Pending location consent, anchored to the message that triggered it. */
  locationConsent?: Parameters<typeof toChatMessage>[1]["locationConsent"];
  /** Brain FE base URL for the "edit in Brain" deep-link (e.g. https://brain.l.gaz.codes). */
  brainBaseUrl?: string;
  /**
   * Called after a user submits an agent-elicited form (tdd-0059).
   *
   * The outcome distinguishes a genuine failure from a duplicate submit —
   * telling someone their answers were lost when they were saved and already
   * acted on is far more alarming than the truth.
   */
  onFormSubmitted?: (formId: string, outcome: SubmitOutcome) => void;
  /** Opens the file picker when the model renders an attachment request. */
  onRequestAttachment?: (mode: "images" | "files" | "any") => void;
  /** The model rendered a LocationRequest and the user pressed Share. */
  onRequestLocation?: (reason?: string, requestId?: string) => void;
  /** User asked for an indexed attachment to be read in full (tdd-0062). */
  onIncludeFullFile?: (attachment: { name: string; path?: string }) => void;
  /** User asked to remove a durably-stored artifact (tdd-0076). */
  onDeleteArtifact?: (attachment: {
    messageId: string;
    attachmentId: string;
    storageKey: string;
  }) => void;
  /** Storage keys with a delete in flight. */
  deletingArtifacts?: ReadonlySet<string>;
  /** User asked to start a fresh edit from the original (tdd-0076). */
  onRevertToOriginal?: (artifact: { rootKey: string; name: string }) => void;
  /** Re-send a user turn whose request failed before any reply arrived. */
  onRetry?: (messageId: string) => void;
  /**
   * Prefix for each message's DOM id, so messages can be linked to.
   *
   * Only set when the host owns a URL — an embedded widget leaves it undefined
   * and renders no ids into someone else's document.
   */
  domIdPrefix?: string;
}

/**
 * The waiting indicator, shown between "sent" and the first token.
 *
 * Built to read as the assistant's reply STARTING rather than as a status
 * message about the app. It occupies the same left-offset column an assistant
 * bubble does (`ml-11` clears the 8-unit avatar plus its gap), so when the
 * first token lands the text appears where the dots already were instead of
 * jumping. The previous version — a spinner in a full-width grey band — sat
 * outside the conversation's own geometry and moved everything when it went.
 *
 * Three staggered dots rather than a spinner: a spinner is the app's idiom for
 * "loading", and this is not a page load, it is someone about to speak. The
 * stagger is what sells it — in phase they read as a progress indicator, out of
 * phase as a wave. `animate-bounce` over `animate-pulse` because a position
 * change survives a glance at low contrast where an opacity fade does not.
 *
 * `motion-reduce` drops the animation to three static dots rather than hiding
 * the indicator: someone who prefers reduced motion still needs to know the
 * assistant is working.
 *
 * Hoisted to module scope so its identity is stable for the whole stream.
 * `aria-live="polite"` announces it once; the dots themselves are decorative.
 */
const THINKING_FOOTER = (
  <div className="flex items-center gap-1 px-4 pb-6 ml-11" aria-live="polite">
    <span className="sr-only">Assistant is thinking</span>
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce motion-reduce:animate-none"
        style={{ animationDelay: `${i * 0.16}s`, animationDuration: "1s" }}
      />
    ))}
  </div>
);

export function Conversation({
  messages,
  isLoading,
  modelId,
  userInfo,
  thinkingDisplayMode = "collapsed",
  groupMessages = true,
  onDeleteMemory,
  locationConsent,
  brainBaseUrl,
  onFormSubmitted,
  onRequestAttachment,
  onRequestLocation,
  onIncludeFullFile,
  onDeleteArtifact,
  deletingArtifacts,
  onRevertToOriginal,
  onRetry,
  domIdPrefix,
}: ConversationProps) {
  // Memoized as ONE unit, deliberately.
  //
  // Every message re-mapped meant a full-text regex pass for <think> blocks, a
  // second pass for json-render fences, and a fresh closure per attachment —
  // all re-run for every message on every streamed token, and all discarded.
  // It also minted new object identities, so no amount of memoizing downstream
  // could stop the whole list re-rendering per token.
  //
  // The Set STAYS INSIDE the memo. Its contract is "one render pass, first
  // occurrence wins, caller owns it" — recomputing rebuilds it from scratch and
  // replays the pass in order, so that holds exactly. Hoisting it out while
  // memoizing the map is the version that breaks: stale ids would leak across
  // passes and silently suppress legitimate forms.
  const chatMessages = useMemo(() => {
    // One set for the whole pass: a form must mount once per conversation, not
    // once per message that happens to carry its spec.
    const renderedFormIds = new Set<string>();
    return messages
      // Filter out empty assistant messages (cancelled generations) and internal
      // tool-role messages; keep assistants that carry tool calls.
      .filter(
        (m) =>
          m.role === "user" ||
          ((m.role === "assistant" || m.role === "system") &&
            (m.content || (m.toolCalls && m.toolCalls.length > 0)))
      )
      .map((m) =>
        toChatMessage(m, {
          renderedFormIds,
          modelId,
          thinkingDisplayMode,
          onDeleteMemory,
          locationConsent,
          brainBaseUrl,
          onIncludeFullFile,
          onDeleteArtifact,
          deletingArtifacts,
          onRevertToOriginal,
          onRetry,
        })
      );
  }, [
    messages,
    modelId,
    thinkingDisplayMode,
    onDeleteMemory,
    locationConsent,
    brainBaseUrl,
    onIncludeFullFile,
    onDeleteArtifact,
    deletingArtifacts,
    onRevertToOriginal,
    onRetry,
  ]);

  // Built once: the renderer subscribes to form submissions on mount, so a new
  // identity every render would tear that subscription down and rebuild it.
  //
  // ALL THREE callbacks must be in the dep array. `onRequestLocation` was
  // captured but omitted, which pinned the first render's copy forever — and
  // that closure reads `geolocation.status`, so both of the guards that depend
  // on it went stale: the "already granted, hand over the cached fix" fast path
  // never fired (the user was re-prompted for a position already in hand), and
  // the "denied, cannot re-prompt" branch never fired either (the chip rendered
  // a button that could not do anything). Callers pass useCallback-stable
  // handlers, so listing them does not cost the memo.
  const genUIRenderer = useMemo(
    () =>
      createGenUIRenderer(
        async (formId, values) => {
          const outcome = await submitFormValues(formId, values);
          // Tell the agent the values are ready. It re-reads them via the MCP
          // tool rather than trusting anything the client sends, so this message
          // carries the form id, not the values — the transcript never repeats
          // what the user typed, and the agent's copy comes from forms-api.
          onFormSubmitted?.(formId, outcome);
        },
        (mode) => onRequestAttachment?.(mode),
        (reason, requestId) => onRequestLocation?.(reason, requestId),
      ),
    [onFormSubmitted, onRequestAttachment, onRequestLocation]
  );

  return (
    <MessageRenderersProvider renderers={chatRenderers}>
      <ComponentRendererProvider renderer={genUIRenderer}>
      <MessageList
        // The document scrolls, not this list (see app/layout.tsx), so it must
        // not open a nested scroll region of its own.
        scrollable={false}
        // The composer is absolutely positioned over the bottom of this scroll
        // area (`bottom-[0%]` in prompt-input), so without trailing space the
        // last message sits underneath it at full scroll and cannot be read.
        // Generous rather than exact: the composer grows with multi-line input,
        // attachment chips and the tool bar, so matching a fixed height would
        // under-pad in exactly the cases where the text matters most.
        contentClassName="pb-40 md:pb-44"
        domIdPrefix={domIdPrefix}
        messages={chatMessages}
        user={userInfo}
        groupMessages={groupMessages}
        isStreaming={isLoading}
        // Hoisted to module scope: rebuilding this tree per render gave the
        // `footer` prop a new identity on every streamed token, which alone
        // would defeat memoizing MessageList.
        footer={isLoading ? THINKING_FOOTER : null}
      />
      </ComponentRendererProvider>
    </MessageRenderersProvider>
  );
}
